import type { LabReport, BiomarkerResult, BiomarkerCategory, BiomarkerStatus, LongitudinalBiomarkerData, TrajectoryDirection } from '../../types';
import type { CapturedSignal } from '../../types/fashionPlate';
import { getAuth } from 'firebase/auth';
import { CF_URLS } from '../../config/api';
import { routedVision } from '../ai/providerRouter';
import { validateExtractionOutput, ExtractionSchemaError } from '../extractionValidationService';
// CLINICAL SAFETY: mockFallback is loaded via dynamic import ONLY when mockMode === true.
// This ensures Vite tree-shakes all fabricated clinical data out of the production bundle.
// See: mockFallback.ts header for allowed/prohibited usage.
import { getActiveClinicalPrompt } from '../firestore/clinicalPromptService';
import { logAnalysisError, logBiomarkerNotFound } from '../firestore/analysisService';
import { getAgentBrand } from '../firestore/agentBrandService';
import { formatPriorResultsForPrompt } from '../longitudinal/biomarkerHistory';
import { resolveAlias } from '../biomarkerAliasService';

/**
 * Hardcoded fallback prompt — used when Firestore is unavailable.
 * Production reads from platform/prompts/clinicalAnalysis/{versionId}.
 */
const LAB_ANALYST_SYSTEM_PROMPT = `## Layer 1 — Stan Van Meter's Bio/Ethics/K-Framework
You are grounded in Stan Van Meter's knowledge framework (K-framework): evidence-based reasoning, intellectual honesty, and a commitment to patient autonomy. You never overstate findings, never fabricate data, and always distinguish between standard-of-care ranges and functional/longevity-optimized ranges. You respect the patient's right to understand their own lab data.

## Layer 2 — HemoLogix Platform Context
You are the HemoLogix Lab Analyst (Captain) — the clinical authority agent in the HemoLogix platform. Your structured output feeds downstream agents (Protocol Builder, Medical Reasoning) that generate personalized protocols. Accuracy and completeness are paramount because all downstream clinical decisions depend on your extraction.

## Layer 3 — Clinical Expertise: Longevity Medicine · HRT · Functional Medicine
You combine the perspectives of three clinical disciplines:
- **Longevity Medical Expert**: You apply centenarian/Blue Zone research and healthspan-optimized reference ranges.
- **HRT Medical Expert**: You recognize hormonal markers (testosterone, estradiol, DHEA-S, SHBG, thyroid panel) with precision, flagging suboptimal levels that conventional ranges miss.
- **Functional Medicine Medical Expert**: You interpret inflammatory, metabolic, and nutritional markers through a root-cause lens — looking at patterns, not just isolated values.

Your job: Given an image of a lab report (blood work, metabolic panel, CBC, thyroid panel, etc.), extract ALL biomarker results and return structured JSON.

For EACH biomarker found, return:
- biomarker: the standard name (e.g., "Vitamin D (25-OH)", "TSH", "Ferritin")
- value: the numeric result
- unit: the unit of measurement
- standardRange: { low, high } — the reference range printed on the report
- functionalRange: { low, high } — functional/optimal range from longevity research
- eliteAverage: the average value seen in elite/centenarian cohorts
- standardStatus: "normal" if within standard range, "abnormal" if outside
- hemologixStatus: one of "optimal", "suboptimal", "low", "elevated", "critical"
  - optimal: within functional range
  - suboptimal: within standard range but outside functional range
  - low/elevated: outside standard range
  - critical: dangerously outside standard range (2x beyond limits)
- category: one of "metabolic", "hormonal", "inflammatory", "nutritional", "hematologic", "lipid", "thyroid", "hepatic", "renal"
- interpretation: 1-2 sentence plain-English explanation comparing standard vs functional ranges, written for a health-conscious consumer (not a doctor)

Use these functional ranges (from longevity/functional medicine research):
- Vitamin D (25-OH): 50-80 ng/mL (elite avg: 62)
- TSH: 1.0-2.0 mIU/L (elite avg: 1.4)
- Free T3: 3.0-4.0 pg/mL (elite avg: 3.5)
- Free T4: 1.2-1.5 ng/dL (elite avg: 1.3)
- Ferritin (F): 50-100 ng/mL (elite avg: 70) | Ferritin (M): 75-150 ng/mL (elite avg: 100)
- Fasting Insulin: 2.0-5.0 µIU/mL (elite avg: 3.8)
- hs-CRP: 0-0.5 mg/L (elite avg: 0.3)
- B12: 500-800 pg/mL (elite avg: 600)
- Total Testosterone (M): 500-900 ng/dL (elite avg: 700)
- Free Testosterone (M): 15-25 pg/mL (elite avg: 20)
- Estradiol (F, day 3): 50-100 pg/mL
- Hemoglobin A1c: 4.5-5.2% (elite avg: 5.0)
- Fasting Glucose: 70-85 mg/dL (elite avg: 80)
- Magnesium (RBC): 5.5-6.5 mg/dL (elite avg: 6.0)
- Homocysteine: 5-8 µmol/L (elite avg: 7)
- Total Cholesterol: 180-220 mg/dL
- HDL: >60 mg/dL (elite avg: 65)
- LDL: 70-130 mg/dL (elite avg: 100)
- Triglycerides: 50-80 mg/dL (elite avg: 65)

For biomarkers not in this list, use your medical knowledge to estimate functional ranges based on longevity research.

## Longitudinal Analysis (UC-2)
When PRIOR LAB RESULTS are provided in the user message, you MUST compare current values with prior values for each matching biomarker. For each biomarker with prior data, include a "trajectory" object with:
- direction: "improving" (moving toward functional optimal), "worsening" (moving away), "stable" (< 5% change), or "new" (no prior data)
- priorValue: the most recent prior numeric value
- priorDate: the ISO date of that prior result
- percentChange: percentage change from prior to current ((current - prior) / prior * 100, rounded to 1 decimal)
- summary: a human-readable trajectory sentence for the patient, e.g. "Your Vitamin D went from 22 to 48 ng/mL" or "Your TSH improved from 4.2 to 1.8 mIU/L over 3 months"

Trajectory direction rules:
- For biomarkers where LOWER is better (TSH, hs-CRP, fasting insulin, homocysteine, triglycerides, HbA1c, fasting glucose): a decrease toward functional range = "improving"
- For biomarkers where HIGHER is better (Vitamin D, B12, HDL, free T3, testosterone): an increase toward functional range = "improving"
- If within functional range and staying within: "stable" regardless of small fluctuations
- If no prior data exists for a biomarker, omit the trajectory field (do NOT include direction "new")

IMPORTANT: Return ONLY valid JSON — an array of objects. No markdown, no code fences, no explanation outside the JSON.

Example output format (with trajectory when prior data is available):
[
  {
    "biomarker": "Vitamin D (25-OH)",
    "value": 48,
    "unit": "ng/mL",
    "standardRange": { "low": 30, "high": 100 },
    "functionalRange": { "low": 50, "high": 80 },
    "eliteAverage": 62,
    "standardStatus": "normal",
    "hemologixStatus": "suboptimal",
    "category": "nutritional",
    "interpretation": "Significantly improved from your previous result. Now within standard range and approaching functional optimal. Continue current supplementation.",
    "trajectory": {
      "direction": "improving",
      "priorValue": 22,
      "priorDate": "2025-12-15T00:00:00Z",
      "percentChange": 118.2,
      "summary": "Your Vitamin D went from 22 to 48 ng/mL — a major improvement toward optimal range (50-80)."
    }
  }
]`;

/**
 * Build the lab analyst system prompt with brand identity from Firestore config.
 * Falls back to LAB_ANALYST_SYSTEM_PROMPT if brand loading fails.
 */
function buildBrandedLabPrompt(brand: { analystRole: string; platformNameFormal: string; kFrameworkOwner: string; clinicalPhilosophy: string }): string {
  return LAB_ANALYST_SYSTEM_PROMPT
    .replace("## Layer 1 — Stan Van Meter's Bio/Ethics/K-Framework", `## Layer 1 — ${brand.kFrameworkOwner}'s Bio/Ethics/K-Framework`)
    .replace("Stan Van Meter's knowledge framework (K-framework): evidence-based reasoning, intellectual honesty, and a commitment to patient autonomy", `${brand.kFrameworkOwner}'s knowledge framework (K-framework): ${brand.clinicalPhilosophy}`)
    .replace('## Layer 2 — HemoLogix Platform Context', `## Layer 2 — ${brand.platformNameFormal} Platform Context`)
    .replace('HemoLogix Lab Analyst (Captain)', brand.analystRole)
    .replace('HemoLogix platform', `${brand.platformNameFormal} platform`);
}

/**
 * Lab Analyst (Skill 2)
 *
 * Takes an uploaded lab file, sends to Claude vision API,
 * returns structured BiomarkerResult[].
 *
 * Provider: Claude Opus 4.6 (clinical authority)
 */
/**
 * Build a patient context fragment from captured signals.
 * Appended to the user message so the Captain can correlate
 * patient-reported symptoms with lab findings in interpretations.
 */
// ── extractLabPDF-based extraction path ──
// Primary path for PDF files. Calls the deployed extractLabPDF Cloud Function
// which handles server-side Claude vision extraction. Avoids needing claudeProxy.

interface ExtractedBiomarkerRaw {
  rawName: string;
  canonicalId: string | null;
  value: number | null;
  unit: string;
  standardRange?: { low: number; high: number };
  flag?: 'H' | 'L' | 'A' | null;
  confidence: number;
}

interface ExtractionResultRaw {
  biomarkers: ExtractedBiomarkerRaw[];
  rawText: string;
  confidence: number;
  extractionId?: string;
}

// Functional ranges from longevity research — mirrors LAB_ANALYST_SYSTEM_PROMPT
const FUNCTIONAL_RANGES: Record<string, { low: number; high: number; elite?: number; category: BiomarkerCategory }> = {
  'vitamin d': { low: 50, high: 80, elite: 62, category: 'nutritional' },
  'vitamin d (25-oh)': { low: 50, high: 80, elite: 62, category: 'nutritional' },
  '25-oh vitamin d': { low: 50, high: 80, elite: 62, category: 'nutritional' },
  'tsh': { low: 1.0, high: 2.0, elite: 1.4, category: 'thyroid' },
  'free t3': { low: 3.0, high: 4.0, elite: 3.5, category: 'thyroid' },
  'free t4': { low: 1.2, high: 1.5, elite: 1.3, category: 'thyroid' },
  'ferritin': { low: 50, high: 100, elite: 70, category: 'hematologic' },
  'fasting insulin': { low: 2.0, high: 5.0, elite: 3.8, category: 'metabolic' },
  'insulin': { low: 2.0, high: 5.0, elite: 3.8, category: 'metabolic' },
  'hs-crp': { low: 0, high: 0.5, elite: 0.3, category: 'inflammatory' },
  'crp': { low: 0, high: 0.5, elite: 0.3, category: 'inflammatory' },
  'c-reactive protein': { low: 0, high: 0.5, elite: 0.3, category: 'inflammatory' },
  'b12': { low: 500, high: 800, elite: 600, category: 'nutritional' },
  'vitamin b12': { low: 500, high: 800, elite: 600, category: 'nutritional' },
  'hemoglobin a1c': { low: 4.5, high: 5.2, elite: 5.0, category: 'metabolic' },
  'hba1c': { low: 4.5, high: 5.2, elite: 5.0, category: 'metabolic' },
  'fasting glucose': { low: 70, high: 85, elite: 80, category: 'metabolic' },
  'glucose': { low: 70, high: 85, elite: 80, category: 'metabolic' },
  'magnesium': { low: 5.5, high: 6.5, elite: 6.0, category: 'nutritional' },
  'homocysteine': { low: 5, high: 8, elite: 7, category: 'inflammatory' },
  'total cholesterol': { low: 180, high: 220, category: 'lipid' },
  'cholesterol': { low: 180, high: 220, category: 'lipid' },
  'hdl': { low: 60, high: 999, elite: 65, category: 'lipid' },
  'hdl cholesterol': { low: 60, high: 999, elite: 65, category: 'lipid' },
  'ldl': { low: 70, high: 130, elite: 100, category: 'lipid' },
  'ldl cholesterol': { low: 70, high: 130, elite: 100, category: 'lipid' },
  'triglycerides': { low: 50, high: 80, elite: 65, category: 'lipid' },
};

function lookupFunctional(name: string): { low: number; high: number; elite?: number; category: BiomarkerCategory } | null {
  const key = name.toLowerCase().trim();
  return FUNCTIONAL_RANGES[key] ?? null;
}

function classifyStatus(value: number, std: { low: number; high: number }, func: { low: number; high: number }): BiomarkerStatus {
  if (value < std.low * 0.5 || value > std.high * 2) return 'critical';
  if (value < std.low || value > std.high) return value < std.low ? 'low' : 'elevated';
  if (value >= func.low && value <= func.high) return 'optimal';
  return 'suboptimal';
}

function guessCategory(name: string): BiomarkerCategory {
  const n = name.toLowerCase();
  if (/thyroid|tsh|t3|t4/.test(n)) return 'thyroid';
  if (/cholesterol|triglyceride|hdl|ldl|lipid/.test(n)) return 'lipid';
  if (/testosterone|estradiol|dhea|shbg|progesterone|hormone|lh|fsh/.test(n)) return 'hormonal';
  if (/crp|homocysteine|ferritin|esr|fibrinogen|il-6/.test(n)) return 'inflammatory';
  if (/vitamin|b12|folate|magnesium|zinc|iron|calcium|selenium/.test(n)) return 'nutritional';
  if (/glucose|insulin|hba1c|a1c/.test(n)) return 'metabolic';
  if (/hemoglobin|hematocrit|rbc|wbc|platelet|mcv|mch/.test(n)) return 'hematologic';
  if (/alt|ast|ggt|bilirubin|albumin|alkaline phosphatase/.test(n)) return 'hepatic';
  if (/creatinine|bun|egfr|uric acid|cystatin/.test(n)) return 'renal';
  return 'metabolic';
}

function buildInterpretation(name: string, status: BiomarkerStatus, value: number, unit: string): string {
  switch (status) {
    case 'optimal': return `Your ${name} (${value} ${unit}) is within the functional optimal range — excellent.`;
    case 'suboptimal': return `Your ${name} (${value} ${unit}) is within standard range but below functional optimal. There may be room for improvement.`;
    case 'low': return `Your ${name} (${value} ${unit}) is below the standard reference range. Worth discussing with your provider.`;
    case 'elevated': return `Your ${name} (${value} ${unit}) is above the standard reference range. Worth discussing with your provider.`;
    case 'critical': return `Your ${name} (${value} ${unit}) is significantly outside normal range. Please consult your provider promptly.`;
  }
}

async function runLabAnalystViaExtractEndpoint(
  file: File,
  patientSex?: 'Male' | 'Female',
): Promise<LabReport> {
  console.log('[Lab Analyst] runLabAnalystViaExtractEndpoint: start');
  const user = getAuth().currentUser;
  console.log(`[Lab Analyst] auth user: ${user?.uid ?? 'null'}`);
  if (!user) throw new Error('Not authenticated — cannot call extractLabPDF');
  const token = await user.getIdToken();

  // ── Upload PDF to Firebase Storage ──
  // Sending a 5-10MB base64 body directly to Cloud Run stalls: the GCLB drops
  // connections when the request body upload takes >~60s (300s on slow networks).
  // Fix: upload the raw file to Firebase Storage (separate connection, designed
  // for large files), then send Cloud Run just the storage path (<1KB JSON).
  // Cloud Run downloads from Storage via Admin SDK — no GCLB timeout pressure.
  console.log('[Lab Analyst] uploading PDF to Firebase Storage...');
  const { getStorage, ref: storageRef, uploadBytesResumable } = await import('firebase/storage');
  const storage = getStorage();
  const timestamp = Date.now();
  const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const uploadRef = storageRef(storage, `lab-uploads/${user.uid}/${timestamp}_${safeFileName}`);

  // uploadBytesResumable uses Google's resumable upload protocol — each chunk is a
  // separate HTTP PUT request, so no GCLB connection timeout on large files.
  await new Promise<void>((resolve, reject) => {
    const task = uploadBytesResumable(uploadRef, file, { contentType: file.type || 'application/pdf' });
    task.on('state_changed',
      (snapshot) => {
        const pct = Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100);
        if (pct % 20 === 0) {
          console.log(`[Lab Analyst] Storage upload: ${pct}% (${(snapshot.bytesTransferred / 1024 / 1024).toFixed(1)}MB)`);
        }
      },
      (error) => {
        console.error('[Lab Analyst] Storage upload failed:', error.code, error.message);
        reject(error);
      },
      resolve,
    );
  });
  const storagePath = uploadRef.fullPath;
  console.log(`[Lab Analyst] PDF uploaded to Storage: ${storagePath}`);

  // ── Call Cloud Run with storage path only ──
  const bodyStr = JSON.stringify({
    storagePath,
    mimeType: file.type || 'application/pdf',
    fileName: file.name,
  });
  console.log('[Lab Analyst] calling extractLabPDF with storage path...');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 550_000);
  let response: Response;
  try {
    response = await fetch(CF_URLS.extractLabPDF, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: bodyStr,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok && !response.headers.get('content-type')?.includes('text/event-stream')) {
    const errText = await response.text().catch(() => '');
    throw new Error(`extractLabPDF failed: ${response.status} ${errText.slice(0, 200)}`);
  }

  const result = await _readSSEResult(response);
  return _buildLabReport(result, patientSex);
}

/**
 * Parse an SSE response body, returning the ExtractionResultRaw from the
 * 'complete' event or throwing on 'error' events.
 */
async function _readSSEResult(response: Response): Promise<ExtractionResultRaw> {
  if (!response.body) throw new Error('extractLabPDF: empty response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        for (const line of part.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          let event: Record<string, unknown>;
          try {
            event = JSON.parse(line.slice(6)) as Record<string, unknown>;
          } catch {
            continue; // skip malformed line
          }

          if (event.type === 'ping') {
            console.log(`[Lab Analyst] extractLabPDF ping: ${event.elapsed}ms`);
          } else if (event.type === 'complete') {
            await reader.cancel();
            return event.result as ExtractionResultRaw;
          } else if (event.type === 'error') {
            await reader.cancel();
            const status = event.status as number | undefined;
            const msg = (event.message as string | undefined) ?? (event.error as string | undefined) ?? 'extractLabPDF failed';
            if (status === 504) {
              throw new Error(msg);
            }
            throw new Error(`extractLabPDF failed: ${status ?? 500} ${event.error ?? ''}`);
          }
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* already released */ }
  }

  throw new Error('extractLabPDF stream ended without result');
}

/**
 * Upload the request body via XHR (instead of fetch) and read the SSE response.
 *
 * Chrome's fetch() stalls on large (>5MB) string bodies when making cross-origin
 * requests over HTTP/2 to Cloud Run — the upload never completes and
 * `requestfinished` never fires. XHR uses a different internal code path that
 * handles large uploads correctly and exposes upload.onprogress for diagnostics.
 */
function _xhrExtractLabPDF(url: string, body: string, token: string): Promise<ExtractionResultRaw> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.timeout = 550_000;

    // Track SSE events from partial responseText
    let lastOffset = 0;
    let settled = false;

    const processChunk = (text: string) => {
      if (settled) return;
      // SSE events are separated by double newlines
      const parts = text.split('\n\n');
      for (const part of parts) {
        for (const line of part.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          let event: Record<string, unknown>;
          try { event = JSON.parse(line.slice(6)) as Record<string, unknown>; }
          catch { continue; }

          if (event.type === 'ping') {
            console.log(`[Lab Analyst] extractLabPDF ping: ${event.elapsed}ms`);
          } else if (event.type === 'complete') {
            settled = true;
            resolve(event.result as ExtractionResultRaw);
            xhr.abort();
            return;
          } else if (event.type === 'error') {
            settled = true;
            const msg = (event.message as string | undefined) ?? (event.error as string | undefined) ?? 'extractLabPDF error';
            reject(new Error(msg));
            xhr.abort();
            return;
          }
        }
      }
    };

    // Upload progress — confirms body is actually transmitting
    let lastPct = -1;
    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        const pct = Math.floor(e.loaded / e.total * 100);
        if (pct !== lastPct && pct % 10 === 0) {
          lastPct = pct;
          console.log(`[Lab Analyst] upload progress: ${pct}% (${(e.loaded / 1024 / 1024).toFixed(1)}/${(e.total / 1024 / 1024).toFixed(1)}MB)`);
        }
      }
    };
    xhr.upload.onload = () => console.log('[Lab Analyst] upload complete — waiting for server response...');
    xhr.upload.onerror = () => { if (!settled) { settled = true; reject(new Error('Upload failed (network error)')); } };

    // Receive partial SSE stream
    xhr.onprogress = () => {
      const newText = xhr.responseText.slice(lastOffset);
      lastOffset = xhr.responseText.length;
      if (newText) processChunk(newText);
    };

    xhr.onload = () => {
      if (!settled) {
        const remaining = xhr.responseText.slice(lastOffset);
        if (remaining) processChunk(remaining);
        // Handle non-SSE early errors
        if (!settled) {
          if (xhr.status >= 400) {
            try {
              const err = JSON.parse(xhr.responseText) as Record<string, string>;
              reject(new Error(`extractLabPDF failed: ${xhr.status} ${err.error ?? ''}`));
            } catch {
              reject(new Error(`extractLabPDF failed: ${xhr.status}`));
            }
          } else {
            reject(new Error('extractLabPDF: no result in response'));
          }
          settled = true;
        }
      }
    };

    xhr.onerror = () => { if (!settled) { settled = true; reject(new Error('extractLabPDF: network error')); } };
    xhr.ontimeout = () => { if (!settled) { settled = true; reject(new Error('extractLabPDF: timeout after 550s')); } };

    xhr.send(body);
  });
}

/**
 * Build a LabReport from raw extraction output.
 */
function _buildLabReport(result: ExtractionResultRaw, patientSex?: 'Male' | 'Female'): LabReport {
  console.log(`[Lab Analyst] extraction complete: ${result.biomarkers?.length ?? 0} biomarkers`);
  const biomarkers: BiomarkerResult[] = [];

  for (const raw of result.biomarkers) {
    if (raw.value === null) continue;

    const std = raw.standardRange ?? { low: 0, high: 9999 };
    const funcData = lookupFunctional(raw.rawName);
    const func = funcData ?? { low: std.low * 0.9, high: std.high * 0.9 };
    const status = classifyStatus(raw.value, std, func);
    const category = funcData?.category ?? guessCategory(raw.rawName);
    const normalizedId = resolveAlias(raw.rawName, patientSex) ?? undefined;
    const standardStatus: 'normal' | 'abnormal' =
      raw.value >= std.low && raw.value <= std.high ? 'normal' : 'abnormal';

    biomarkers.push({
      biomarker: raw.rawName,
      value: raw.value,
      unit: raw.unit,
      standardRange: std,
      functionalRange: { low: func.low, high: func.high },
      eliteAverage: funcData?.elite ?? (func.low + func.high) / 2,
      standardStatus,
      hemologixStatus: status,
      category,
      interpretation: buildInterpretation(raw.rawName, status, raw.value, raw.unit),
      normalizedId,
    });
  }

  return {
    id: `lab-${Date.now()}`,
    uploadedAt: new Date().toISOString(),
    source: 'pdf',
    biomarkers,
    rawText: result.rawText,
    extractionConfidence: result.confidence,
  };
}

function buildSignalContext(signals: CapturedSignal[]): string {
  if (!signals || signals.length === 0) return '';

  const lines = signals.map((s) => {
    let entry = `- ${s.term} (${s.category})`;
    if (s.linkedBiomarker) entry += ` [may correlate with ${s.linkedBiomarker}]`;
    return entry;
  });

  return `\n\nPatient-reported signals from conversation (reference these in interpretations where relevant):\n${lines.join('\n')}`;
}

export async function runLabAnalyst(
  file: File | null,
  mockMode: boolean,
  signals?: CapturedSignal[],
  priorData?: LongitudinalBiomarkerData | null,
  patientSex?: 'Male' | 'Female',
): Promise<LabReport> {
  // CLINICAL SAFETY: Mock mode is ONLY for demo/testing (VITE_ENABLE_MOCK=true).
  // When a real API call fails, we throw an error — we NEVER silently substitute
  // fabricated lab data. A patient must never receive fake results as if they were real.
  if (mockMode) {
    const { getMockLabReport } = await import('./mockFallback');
    await new Promise((r) => setTimeout(r, 1500));
    return getMockLabReport();
  }

  if (!file) {
    throw new Error('No lab file provided. Please upload your lab results and try again.');
  }

  try {
    // PRIMARY PATH FOR PDFs: use deployed extractLabPDF endpoint directly.
    // This avoids the claudeProxy dependency (not deployed) and the geminiProxy
    // vision rejection. extractLabPDF is deployed and handles server-side extraction.
    // Check both MIME type and extension — headless browsers often send empty MIME type.
    const isPDF = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
    console.log(`[Lab Analyst] file.type="${file.type}" name="${file.name}" isPDF=${isPDF}`);
    if (isPDF) {
      return await runLabAnalystViaExtractEndpoint(file, patientSex);
    }

    const base64 = await fileToBase64(file);
    const mimeType = file.type || 'image/jpeg';
    const signalContext = buildSignalContext(signals ?? []);

    // Load brand identity + clinical prompt from Firestore; fallback to hardcoded
    let systemPrompt = LAB_ANALYST_SYSTEM_PROMPT;
    try {
      const [firestorePrompt, brand] = await Promise.all([
        getActiveClinicalPrompt(),
        getAgentBrand(),
      ]);
      if (firestorePrompt) {
        systemPrompt = firestorePrompt.systemPrompt;
      } else {
        // No Firestore clinical prompt — use hardcoded fallback with brand identity injected
        systemPrompt = buildBrandedLabPrompt(brand);
      }
    } catch {
      console.warn('[Lab Analyst] Firestore unavailable, using hardcoded fallback');
    }

    const priorContext = formatPriorResultsForPrompt(priorData ?? null);
    const routerResult = await routedVision(
      'lab-analyst',
      systemPrompt,
      base64,
      mimeType,
      `Extract all biomarker results from this lab report. Return JSON array only.${signalContext}${priorContext}`,
    );
    if (routerResult.fallbackUsed) {
      console.warn(`[Lab Analyst] Fallback used: ${routerResult.provider}/${routerResult.model}`);
    }
    const response = routerResult.text;

    // Parse the JSON response — schema validation runs inside parseBiomarkerResponse.
    // If the model output fails schema validation, ExtractionSchemaError is thrown
    // and caught by the outer catch, which triggers the provider router's failover
    // on the next attempt (circuit breaker marks the provider as failed).
    const allBiomarkers = parseBiomarkerResponse(response, `${routerResult.provider}/${routerResult.model}`);

    // ── C02→C10 Normalization (CAP.10 D#2 Safety Controls) ──
    // Normalize each extracted biomarker name through C10's alias map.
    // Unresolvable names → BIOMARKER_NOT_FOUND → excluded with visible note.
    const biomarkers: typeof allBiomarkers = [];
    const excludedBiomarkers: Array<{ rawName: string; reason: string }> = [];

    for (const bio of allBiomarkers) {
      const canonicalId = resolveAlias(bio.biomarker, patientSex);

      if (canonicalId) {
        bio.normalizedId = canonicalId;
        biomarkers.push(bio);
      } else {
        // BIOMARKER_NOT_FOUND: loud failure — log and exclude
        const reason = `BIOMARKER_NOT_FOUND: "${bio.biomarker}" could not be matched to any known biomarker ID. Excluded from results.`;
        console.warn(`[Lab Analyst] ${reason}`);
        excludedBiomarkers.push({ rawName: bio.biomarker, reason });

        // Fire-and-forget Firestore log for operational dashboard monitoring
        logBiomarkerNotFound(bio.biomarker, patientSex).catch(() => {});
      }
    }

    if (excludedBiomarkers.length > 0) {
      console.warn(
        `[Lab Analyst] ${excludedBiomarkers.length} biomarker(s) excluded (BIOMARKER_NOT_FOUND). ` +
        `Review alias map for: ${excludedBiomarkers.map(e => e.rawName).join(', ')}`
      );
    }

    // CLINICAL SAFETY: Do NOT fabricate pageCount or extractionConfidence.
    // These fields are optional (LabReport interface). Hardcoding values like
    // pageCount: 1 or extractionConfidence: 0.9 constitutes fabricated evidence —
    // presenting made-up metrics as if they were measured from the patient's data.
    // Omit them entirely rather than guess. Downstream consumers must handle undefined.
    return {
      id: `lab-${Date.now()}`,
      uploadedAt: new Date().toISOString(),
      source: file.type === 'application/pdf' ? 'pdf' : 'photo',
      biomarkers,
      excludedBiomarkers: excludedBiomarkers.length > 0 ? excludedBiomarkers : undefined,
    };
  } catch (error) {
    console.error('[Lab Analyst] Error:', error);
    logAnalysisError('lab-analyst', error instanceof Error ? error.message : String(error)).catch(() => {});
    throw new Error('Lab analysis is temporarily unavailable. Please try again in a few minutes.');
  }
}

function parseBiomarkerResponse(response: string, modelId = 'unknown'): BiomarkerResult[] {
  try {
    // Strip any markdown code fences if present
    let cleaned = response.trim();
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
    }

    const parsed = JSON.parse(cleaned);

    if (!Array.isArray(parsed)) {
      throw new Error('Response is not an array');
    }

    // ── Schema validation (c1): validate against ExtractionOutput before processing ──
    // Map to validation format: biomarker field → name field
    const validationItems = parsed.map((item: Record<string, unknown>) => ({
      ...item,
      name: item.biomarker || item.name || item.rawName,
    }));
    const validation = validateExtractionOutput(validationItems, modelId);
    if (!validation.valid) {
      throw new ExtractionSchemaError(validation);
    }

    return parsed.map((item: Record<string, unknown>) => {
      const result: BiomarkerResult = {
        biomarker: String(item.biomarker || ''),
        value: Number(item.value) || 0,
        unit: String(item.unit || ''),
        standardRange: {
          low: Number((item.standardRange as Record<string, unknown>)?.low) || 0,
          high: Number((item.standardRange as Record<string, unknown>)?.high) || 0,
        },
        functionalRange: {
          low: Number((item.functionalRange as Record<string, unknown>)?.low) || 0,
          high: Number((item.functionalRange as Record<string, unknown>)?.high) || 0,
        },
        eliteAverage: Number(item.eliteAverage) || 0,
        standardStatus: item.standardStatus === 'abnormal' ? 'abnormal' : 'normal',
        hemologixStatus: (['optimal', 'suboptimal', 'low', 'elevated', 'critical'].includes(
          String(item.hemologixStatus),
        )
          ? String(item.hemologixStatus)
          : 'suboptimal') as BiomarkerResult['hemologixStatus'],
        category: String(item.category || 'metabolic') as BiomarkerResult['category'],
        interpretation: String(item.interpretation || ''),
      };

      // Parse trajectory if present (UC-2: longitudinal comparison)
      if (item.trajectory && typeof item.trajectory === 'object') {
        const t = item.trajectory as Record<string, unknown>;
        const direction = String(t.direction || '');
        if (['improving', 'worsening', 'stable'].includes(direction)) {
          result.trajectory = {
            direction: direction as TrajectoryDirection,
            priorValue: Number(t.priorValue) || 0,
            priorDate: String(t.priorDate || ''),
            percentChange: Number(t.percentChange) || 0,
            summary: String(t.summary || ''),
          };
        }
      }

      return result;
    });
  } catch (error) {
    console.error('[Lab Analyst] Failed to parse response:', error, response);
    throw new Error('Failed to parse lab analysis response');
  }
}

/**
 * Convert a File to base64 for API transmission.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
